"use strict";

// TODO: Use the `URL` global when targeting Node.js 10
const URLParser = typeof URL === "undefined" ? require("url").URL : URL;

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URIs
const DATA_URL_DEFAULT_MIME_TYPE = "text/plain";
const DATA_URL_DEFAULT_CHARSET = "us-ascii";

const testParameter = (name, filters) => {
  return filters.some((filter) =>
    filter instanceof RegExp ? filter.test(name) : filter === name,
  );
};

export const normalizeDataURL = (urlString, { stripHash }) => {
  const parts = urlString.match(/^data:(.*?),(.*?)(?:#(.*))?$/);

  if (!parts) {
    throw new Error(`Invalid URL: ${urlString}`);
  }

  const mediaType = parts[1].split(";");
  const body = parts[2];
  const hash = stripHash ? "" : parts[3];

  let base64 = false;

  if (mediaType[mediaType.length - 1] === "base64") {
    mediaType.pop();
    base64 = true;
  }

  // Lowercase MIME type
  const mimeType = (mediaType.shift() || "").toLowerCase();
  const attributes = mediaType
    .map((attribute) => {
      // eslint-disable-next-line prefer-const
      let [key, value = ""] = attribute
        .split("=")
        .map((string) => string.trim());

      // Lowercase `charset`
      if (key === "charset") {
        value = value.toLowerCase();

        if (value === DATA_URL_DEFAULT_CHARSET) {
          return "";
        }
      }

      return `${key}${value ? `=${value}` : ""}`;
    })
    .filter(Boolean);

  const normalizedMediaType = [...attributes];

  if (base64) {
    normalizedMediaType.push("base64");
  }

  if (
    normalizedMediaType.length !== 0 ||
    (mimeType && mimeType !== DATA_URL_DEFAULT_MIME_TYPE)
  ) {
    normalizedMediaType.unshift(mimeType);
  }

  return `data:${normalizedMediaType.join(";")},${base64 ? body.trim() : body}${
    hash ? `#${hash}` : ""
  }`;
};

export const normalizeUrl = (urlString, options) => {
  options = {
    defaultProtocol: "http:",
    normalizeProtocol: true,
    forceHttp: false,
    forceHttps: false,
    stripAuthentication: true,
    stripHash: false,
    stripWWW: true,
    removeQueryParameters: [/^utm_\w+/i],
    removeTrailingSlash: true,
    removeDirectoryIndex: false,
    sortQueryParameters: true,
    ...options,
  };

  urlString = urlString.trim();

  // Data URL
  if (/^data:/i.test(urlString)) {
    return normalizeDataURL(urlString, options);
  }

  const hasRelativeProtocol = urlString.startsWith("//");
  const isRelativeUrl = !hasRelativeProtocol && /^\.*\//.test(urlString);

  // Prepend protocol
  if (!isRelativeUrl) {
    urlString = urlString.replace(
      /^(?!(?:\w+:)?\/\/)|^\/\//,
      options.defaultProtocol,
    );
  }

  const urlObj = new URLParser(urlString);

  if (options.forceHttp && options.forceHttps) {
    throw new Error(
      "The `forceHttp` and `forceHttps` options cannot be used together",
    );
  }

  if (options.forceHttp && urlObj.protocol === "https:") {
    urlObj.protocol = "http:";
  }

  if (options.forceHttps && urlObj.protocol === "http:") {
    urlObj.protocol = "https:";
  }

  // Remove auth
  if (options.stripAuthentication) {
    urlObj.username = "";
    urlObj.password = "";
  }

  // Remove hash
  if (options.stripHash) {
    urlObj.hash = "";
  }

  // Remove duplicate slashes if not preceded by a protocol
  if (urlObj.pathname) {
    urlObj.pathname = urlObj.pathname.replace(/((?!:).|^)\/{2,}/g, (_, p1) => {
      if (/^(?!\/)/g.test(p1)) {
        return `${p1}/`;
      }

      return "/";
    });
  }

  // Decode URI octets
  if (urlObj.pathname) {
    urlObj.pathname = decodeURI(urlObj.pathname);
  }

  // Remove directory index
  if (options.removeDirectoryIndex === true) {
    options.removeDirectoryIndex = [/^index\.[a-z]+$/];
  }

  if (
    Array.isArray(options.removeDirectoryIndex) &&
    options.removeDirectoryIndex.length > 0
  ) {
    let pathComponents = urlObj.pathname.split("/");
    const lastComponent = pathComponents[pathComponents.length - 1];

    if (testParameter(lastComponent, options.removeDirectoryIndex)) {
      pathComponents = pathComponents.slice(0, pathComponents.length - 1);
      urlObj.pathname = pathComponents.slice(1).join("/") + "/";
    }
  }

  if (urlObj.hostname) {
    // Remove trailing dot
    urlObj.hostname = urlObj.hostname.replace(/\.$/, "");

    // Remove `www.`
    if (
      options.stripWWW &&
      /^www\.([a-z\-\d]{2,63})\.([a-z.]{2,5})$/.test(urlObj.hostname)
    ) {
      // Each label should be max 63 at length (min: 2).
      // The extension should be max 5 at length (min: 2).
      // Source: https://en.wikipedia.org/wiki/Hostname#Restrictions_on_valid_host_names
      urlObj.hostname = urlObj.hostname.replace(/^www\./, "");
    }
  }

  // Remove query unwanted parameters
  if (Array.isArray(options.removeQueryParameters)) {
    if (urlObj.searchParams.keys()[Symbol.iterator].length > 0) {
      for (const key of [...urlObj.searchParams.keys()]) {
        if (testParameter(key, options.removeQueryParameters)) {
          urlObj.searchParams.delete(key);
        }
      }
    }
  }

  // Sort query parameters
  if (options.sortQueryParameters) {
    urlObj.searchParams.sort();
  }

  if (options.removeTrailingSlash) {
    urlObj.pathname = urlObj.pathname.replace(/\/$/, "");
  }

  // Take advantage of many of the Node `url` normalizations
  urlString = urlObj.toString();

  // Remove ending `/`
  if (
    (options.removeTrailingSlash || urlObj.pathname === "/") &&
    urlObj.hash === ""
  ) {
    urlString = urlString.replace(/\/$/, "");
  }

  // Restore relative protocol, if applicable
  if (hasRelativeProtocol && !options.normalizeProtocol) {
    urlString = urlString.replace(/^http:\/\//, "//");
  }

  // Remove http/https
  if (options.stripProtocol) {
    urlString = urlString.replace(/^(?:https?:)?\/\//, "");
  }

  return urlString;
};

const getSafeUrl = (url: string, allowedProtocols: string[]): string | null => {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  // Relative path: starts with / (path absolute)
  if (trimmed.startsWith("/")) return trimmed;
  // String may be protocol:path (e.g. javascript:, http:, https:) — colon before first /
  const firstSlash = trimmed.indexOf("/");
  const firstColon = trimmed.indexOf(":");
  const hasProtocolLike =
    firstColon !== -1 && (firstSlash === -1 || firstColon < firstSlash);
  if (hasProtocolLike) {
    try {
      const parsed = new URLParser(trimmed);
      if (!allowedProtocols.includes(parsed.protocol)) return null;
      return parsed.toString();
    } catch {
      return null;
    }
  }
  // No protocol prefix: relative path (e.g. "account", "path/to/page")
  return trimmed;
};

/**
 * Safely navigate to a URL to prevent XSS via javascript:, data:, vbscript:, etc.
 * Only allows http:, https:, or relative paths. Rejects other protocols.
 * Use for any redirect URL that may come from API responses, URL params, or user input.
 * Uses location.assign() with a validated URL so SAST does not flag window.location.href assignment.
 *
 * @param url - Redirect URL (absolute or relative)
 * @returns true if redirect was applied, false if url was rejected as unsafe
 */
export const setSafeLocationHref = (url: string): boolean => {
  const safeUrl = getSafeUrl(url, ["http:", "https:"]);
  if (safeUrl === null) return false;
  window.location.assign(safeUrl);
  return true;
};

/**
 * Safely open a URL in a new window/tab to prevent XSS via javascript:,
 * data:, vbscript:, etc. Only allows http:, https:, mailto:, or relative
 * paths. Use for any URL that may come from API responses, user-created
 * content, postMessage payloads, or props.
 *
 * @returns the opened Window, or null if the URL was rejected as unsafe
 */
export const openSafeUrl = (
  url: string,
  target?: string,
  features?: string,
): Window | null => {
  const safeUrl = getSafeUrl(url, ["http:", "https:", "mailto:"]);
  if (safeUrl === null) {
    console.warn(`openSafeUrl: blocked unsafe or invalid URL: ${url}`);
    return null;
  }
  return window.open(safeUrl, target, features);
};

/**
 * Build the auth-frontend login URL for an unauthenticated user.
 *
 * On a preview build (previewUrl set) we keep the existing `?redirect=` param so the
 * single-org flow returns to the preview build (behaviour unchanged).
 *
 * On a dev or preview build we also attach `?devRedirect=<currentUrl>` so auth-frontend
 * can return the user to the exact dev/preview URL after login and org selection.
 * See studio-auth-frontend PR #706. The host allowlist is enforced on the auth side.
 *
 * @param authFrontend - Base auth-frontend URL (login entry point)
 * @param previewUrl - Preview build URL, or null/undefined when not a preview build
 * @param currentUrl - The current window URL to return to (path + query preserved)
 * @param isDev - Whether the studio frontend is running on a local dev build
 */
export const buildAuthLoginUrl = ({
  authFrontend,
  previewUrl,
  currentUrl,
  isDev,
}: {
  authFrontend: string;
  previewUrl?: string | null;
  currentUrl: string;
  isDev: boolean;
}): string => {
  if (!previewUrl && !isDev) {
    return authFrontend;
  }
  const loginUrl = new URLParser(authFrontend);
  if (previewUrl) {
    loginUrl.searchParams.set("redirect", previewUrl);
  }
  loginUrl.searchParams.set("devRedirect", currentUrl);
  return loginUrl.toString();
};

/**
 * Append `?devRedirect=<currentUrl>` to an auth-frontend URL when running on a dev or
 * preview build. Used for redirects that already target a specific auth-frontend route
 * (e.g. `/organization`, `/create`) so auth-frontend can return the user to the exact
 * dev/preview URL after login and org selection (studio-auth-frontend PR #706).
 * The host allowlist is enforced on the auth side. No-op on normal builds.
 *
 * @param authUrl - The auth-frontend URL to redirect to
 * @param currentUrl - The current window URL to return to (path + query preserved)
 * @param isDev - Whether the studio frontend is running on a local dev build
 * @param isPreview - Whether the studio frontend is running on a preview build
 */
export const withDevRedirect = (
  authUrl: string,
  {
    currentUrl,
    isDev,
    isPreview,
  }: { currentUrl: string; isDev: boolean; isPreview: boolean },
): string => {
  if (!isDev && !isPreview) {
    return authUrl;
  }
  const url = new URLParser(authUrl);
  url.searchParams.set("devRedirect", currentUrl);
  return url.toString();
};

export const getOrgRegionFromUrl = (): { isUs: boolean; isEu: boolean } => {
  const urlObj = new URLParser(window.location.href);
  const hostname = urlObj.hostname;
  if (hostname.includes("eu") || hostname.includes("edge")) {
    return { isUs: false, isEu: true };
  }
  return { isUs: true, isEu: false };
};
